import {WithdrawablePaymentMethod} from '../../../../graphql/types'
import {TauroPayoutLeg} from '../tauroWithdrawal'
import {getPlatform} from '../../../../common/utils'
import {WithdrawalMethodValues} from './constants'
import {
  CreateWithdrawalItem,
  DepositMethodIssueVerificationType,
  RefundWithdrawSchemaItem,
} from './types'
import {
  PixAccountTypes,
  WithdrawalVendorEnumIndices,
  withdrawalPaymentVendors,
  countries,
} from '@bdswiss/common-enums'

type BuildRefundWithdrawSchemaResult = {
  totalRemainingAmount: number
  totalAmountToRefund: number
  refundWithdrawSchema: RefundWithdrawSchemaItem[]
}

function buildRefundWithdrawSchema({
  withdrawablePaymentMethods,
  amountToWithdraw,
  depositMethodIssueVerifications,
}: {
  withdrawablePaymentMethods: WithdrawablePaymentMethod[]
  amountToWithdraw: number
  depositMethodIssueVerifications: DepositMethodIssueVerificationType[]
}): BuildRefundWithdrawSchemaResult {
  const withdrawablePaymentMethodIdsWithAcceptedIssue = depositMethodIssueVerifications
    .filter(({processStatus}) => processStatus === 'accepted')
    .map(({paymentMethodOptionId}) => paymentMethodOptionId)

  const refundWithdrawSchema: RefundWithdrawSchemaItem[] = []
  let totalRemainingAmount = amountToWithdraw

  for (const methodToRefund of withdrawablePaymentMethods) {
    const {amountLeft, cardNumber, vendor, id} = methodToRefund
    const withAcceptedIssue = id && withdrawablePaymentMethodIdsWithAcceptedIssue.includes(id)
    const withAmountToWithdrawLeft = Number(amountLeft) > 0

    if (totalRemainingAmount <= 0 || !id || withAcceptedIssue || !withAmountToWithdrawLeft) {
      continue
    }

    let details: string

    switch (vendor as string) {
      case WithdrawalVendorEnumIndices.enovipayRefund:
      case WithdrawalVendorEnumIndices.creditDebitCard:
      case withdrawalPaymentVendors.creditDebitCard.value:
        if (!cardNumber) continue
        details = cardNumber
        break
      case WithdrawalVendorEnumIndices.enovipayMobileWalletRefund:
        details = 'Apple pay / Google pay'
        break
      case WithdrawalVendorEnumIndices.enovipayKnetRefund:
        details = 'Knet'
        break
      default:
        continue
    }

    const amount =
      totalRemainingAmount >= Number(amountLeft) ? Number(amountLeft) : totalRemainingAmount

    refundWithdrawSchema.push({
      amount,
      details,
      vendor: vendor as string,
      id,
    })

    totalRemainingAmount = totalRemainingAmount - Number(amountLeft)
  }

  const totalAmountToRefund = refundWithdrawSchema.reduce((acc, item) => {
    acc = acc + item.amount
    return acc
  }, 0)
  return {
    totalRemainingAmount: totalRemainingAmount > 0 ? totalRemainingAmount : 0,
    refundWithdrawSchema,
    totalAmountToRefund,
  }
}

function buildWithdrawalsParams({
  account,
  refundWithdrawSchema,
  activeWithdrawalMethod,
  bankTransferPaymentFields,
  cryptoPaymentFields,
  cryptoPayoutPaymentFields,
  dfnsCryptoPayoutPaymentFields,
  payRetailersPaymentFields,
  pixPayoutPaymentFields,
  totalRemainingAmount,
  withTauroPayoutLegs = false,
}): CreateWithdrawalItem[] {
  const params: CreateWithdrawalItem[] = []
  const platform = JSON.stringify(getPlatform())

  for (const refundItem of refundWithdrawSchema) {
    let paymentFields: {termsAndConditions: string; cardNumber?: string} | null = null

    switch (refundItem.vendor as string) {
      case WithdrawalVendorEnumIndices.enovipayRefund:
      case WithdrawalVendorEnumIndices.creditDebitCard:
      case withdrawalPaymentVendors.creditDebitCard.value: {
        if (!refundItem.details) continue

        paymentFields = {
          cardNumber: refundItem.details,
          termsAndConditions: 'accept',
        }
        break
      }
      case WithdrawalVendorEnumIndices.enovipayMobileWalletRefund:
      case WithdrawalVendorEnumIndices.enovipayKnetRefund: {
        paymentFields = {
          termsAndConditions: 'accept',
        }
        break
      }
      default:
        continue
    }

    params.push({
      amount: Number(refundItem.amount),
      paymentVendor: refundItem.vendor,
      paymentFields,
      accountId: account.id,
      platform,
    })
  }
  const refundItemsCount = params.length

  if (activeWithdrawalMethod === WithdrawalMethodValues.bankTransfer && bankTransferPaymentFields) {
    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.bankTransfer,
      paymentFields: {
        ...bankTransferPaymentFields,
        termsAndConditions: 'accept',
      },
      accountId: account.id,
      platform,
    })
  }

  if (activeWithdrawalMethod === WithdrawalMethodValues.cryptocurrency && cryptoPaymentFields) {
    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.cryptocurrency,
      paymentFields: {
        ...cryptoPaymentFields,
        // Convert boolean to string for GraphQL strict typing (GraphQL 16.x requires strict types)
        walletResponsibilityConfirmation:
          typeof cryptoPaymentFields.walletResponsibilityConfirmation === 'boolean'
            ? String(cryptoPaymentFields.walletResponsibilityConfirmation)
            : cryptoPaymentFields.walletResponsibilityConfirmation || '',
        termsAndConditions: 'accept',
      },
      accountId: account.id,
      platform,
    })
  }

  if (
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayCryptoPayout &&
    cryptoPayoutPaymentFields
  ) {
    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.enovipayCryptoPayout,
      paymentFields: {
        ...cryptoPayoutPaymentFields,
        // Convert boolean to string for GraphQL strict typing (GraphQL 16.x requires strict types)
        walletResponsibilityConfirmation:
          typeof cryptoPayoutPaymentFields.walletResponsibilityConfirmation === 'boolean'
            ? String(cryptoPayoutPaymentFields.walletResponsibilityConfirmation)
            : cryptoPayoutPaymentFields.walletResponsibilityConfirmation || '',
        termsAndConditions: 'accept',
      },
      accountId: account.id,
      platform,
    })
  }

  if (
    activeWithdrawalMethod === WithdrawalMethodValues.dfnsCryptoPayout &&
    dfnsCryptoPayoutPaymentFields
  ) {
    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.dfnsCryptoPayout,
      paymentFields: {
        ...dfnsCryptoPayoutPaymentFields,
        // Convert boolean to string for GraphQL strict typing (GraphQL 16.x requires strict types)
        walletResponsibilityConfirmation:
          typeof dfnsCryptoPayoutPaymentFields.walletResponsibilityConfirmation === 'boolean'
            ? String(dfnsCryptoPayoutPaymentFields.walletResponsibilityConfirmation)
            : dfnsCryptoPayoutPaymentFields.walletResponsibilityConfirmation || '',
        termsAndConditions: 'accept',
      },
      accountId: account.id,
      platform,
    })
  }

  if (
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayPixPayout &&
    pixPayoutPaymentFields
  ) {
    const fields = {
      ...pixPayoutPaymentFields,
      termsAndConditions: 'accept',
    }
    switch (pixPayoutPaymentFields.pixAccountType) {
      case PixAccountTypes.cpf.value:
      case PixAccountTypes.cnpj.value:
        fields.pixAccountId = fields.pixCustomerIdentity
        break
      case PixAccountTypes.phone.value:
        fields.pixAccountId = `+55${fields.pixAccountId}`
        break
    }

    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.enovipayPixPayout,
      paymentFields: fields,
      accountId: account.id,
      platform,
    })
  }

  if (activeWithdrawalMethod === WithdrawalMethodValues.payRetailers && payRetailersPaymentFields) {
    params.push({
      amount: Number(totalRemainingAmount),
      paymentVendor: WithdrawalMethodValues.payRetailers,
      paymentFields: {
        ...payRetailersPaymentFields,
        documentType: payRetailersPaymentFields.documentType.toString(),
        termsAndConditions: 'accept',
      },
      accountId: account.id,
      platform,
    })
  }

  // PT-6618: a Tauro account sends each item as a payout leg; the payout vendor carries the profit.
  if (!withTauroPayoutLegs) return params
  return params.map((item, index) => ({
    ...item,
    tauroPayoutLeg: index < refundItemsCount ? TauroPayoutLeg.refund : TauroPayoutLeg.profit,
  }))
}

function getNoReadRejectedId({
  refundWithdrawSchema,
  depositMethodIssueVerifications,
}: {
  refundWithdrawSchema: RefundWithdrawSchemaItem[]
  depositMethodIssueVerifications: DepositMethodIssueVerificationType[]
}) {
  const availableWithdrawablePaymentOptionIds = refundWithdrawSchema.map(({id}) => id)

  const actualDepositMethodIssueVerificationsRejectedUnread =
    depositMethodIssueVerifications.filter(({processStatus, paymentMethodOptionId, meta}) => {
      const isAvailableForWithdraw =
        availableWithdrawablePaymentOptionIds.includes(paymentMethodOptionId)
      const isRejected = processStatus === 'rejected'
      const isRejectAtReadValue = isRejectedAtRead(meta)
      const isRejectAtNoRead = !isRejectAtReadValue

      return isAvailableForWithdraw && isRejected && isRejectAtNoRead
    })

  return actualDepositMethodIssueVerificationsRejectedUnread.length
    ? actualDepositMethodIssueVerificationsRejectedUnread[0].id
    : null
}

function getIsIssueInReview({
  refundWithdrawSchema,
  depositMethodIssueVerifications,
}: {
  refundWithdrawSchema: RefundWithdrawSchemaItem[]
  depositMethodIssueVerifications: DepositMethodIssueVerificationType[]
}) {
  const availableWithdrawablePaymentOptionIds = refundWithdrawSchema.map(({id}) => id)

  const actualDepositMethodIssueVerificationsInReview = depositMethodIssueVerifications.filter(
    ({processStatus, paymentMethodOptionId}) => {
      const isAvailableForWithdraw =
        availableWithdrawablePaymentOptionIds.includes(paymentMethodOptionId)
      const isInReview = processStatus === 'inReview'

      return isInReview && isAvailableForWithdraw
    },
  )

  return Boolean(actualDepositMethodIssueVerificationsInReview.length)
}

function isRejectedAtRead(meta) {
  if (!meta) return false

  try {
    const parsedMeta = JSON.parse(meta)

    return Boolean(parsedMeta.rejectedInfoReadAt) ?? false
  } catch (e) {
    return false
  }
}

function getWithdrawalMethodMinAmount(
  method: WithdrawalMethodValues | null,
  clientCountry?: string | null,
): number | null {
  if (!method) return null

  switch (method) {
    case WithdrawalMethodValues.bankTransfer: {
      const shouldApplyReducedRule = [countries.gb.value, countries.kw.value].includes(
        clientCountry?.toUpperCase() ?? '',
      )
      return shouldApplyReducedRule ? 30 : 100
    }

    case WithdrawalMethodValues.dfnsCryptoPayout:
      return 20
    case WithdrawalMethodValues.enovipayPixPayout:
      return 50
    case WithdrawalMethodValues.payRetailers:
      return 10
    default:
      return null
  }
}

export {
  getNoReadRejectedId,
  getWithdrawalMethodMinAmount,
  buildRefundWithdrawSchema,
  buildWithdrawalsParams,
  getIsIssueInReview,
}
